import { dollarsToCents } from '@core/util/money';
import { getSearchItemName } from '@core/util/teranet';
import { BusinessClient, ClientType, PersonClient } from '@models/clients';
import { Debt, MatterClientType, MatterFileType } from '@models/matter';
import { MatterRequisitionPrecedent } from '@models/requisitions';
import {
	SavedWrit,
	WritName,
	WritSearchItem,
	WritSearchStatus,
	WritSearchType,
} from '@models/teranet';
import { Selector } from '@ngxs/store';
import { UrlService } from '@services/url/url.service';
import { Clients, MatterClients } from '@state/global/matter_clients';
import { DebtsState } from '@state/matter/debts';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterRequisitionsState } from '@state/matter/requisitions/matter-requisitions.state';
import { SaleWritState } from '@state/sale_writ';
import { PurchaseStateType, PurchaseStates } from '@state/utils/models';
import { cloneDeep } from 'lodash';
import {
	GroupedWrit,
	NamesViewModel,
	WritSearch,
} from './results.component.models';
import { Logger } from '@core/logger';

export class TeranetWritResultSelectors {
	@Selector([
		SaleWritState.savedItems,
		SaleWritState.executionPurchaseStates,
		SelectedMatter.matterFileType,
		SelectedMatter.id,
		MatterRequisitionsState.getAll,
		DebtsState.getAll,
		MatterClients.get,
	])
	static groupByName(
		savedWrits: SavedWrit[],
		executionStates: PurchaseStates,
		matterFileType: MatterFileType,
		matterId: string,
		requisitions: MatterRequisitionPrecedent[],
		debts: Debt[],
		clients: Clients[],
	): { [id: string]: GroupedWrit } {
		const _savedWrits = cloneDeep(savedWrits);

		const grouped: { [id: string]: GroupedWrit } = {};

		const filter =
			matterFileType === MatterFileType.SALE
				? [MatterClientType.SELLER]
				: [
						MatterClientType.BUYER,
						MatterClientType.SELLER,
						MatterClientType.BORROWER,
						MatterClientType.GUARANTOR,
					];

		clients
			.filter(
				(x) =>
					(x.name || (x as PersonClient).firstName) &&
					x.clientTypes?.some((y) => filter.includes(y)),
			)
			.forEach((client) => {
				const _emptyGroup = this.getEmptyClientWrit(client);
				grouped[_emptyGroup.name?.toLowerCase()] = _emptyGroup;
			});

		if (!_savedWrits || _savedWrits.length == 0) {
			return grouped;
		}

		const indexedSavedWrits = [];
		_savedWrits.map((x) => (indexedSavedWrits[x.id] = x));

		const result = _savedWrits.reduce((prev, curr) => {
			if (!curr.search) {
				Logger.info('No search object on current writ.', curr);
				return prev;
			}

			const name = getSearchItemName(curr.search).trim();

			let match = prev[name.toLowerCase()];
			const id = name?.toLowerCase();

			if (!match) {
				prev[id] = this.buildGroupedWrit(
					id,
					name,
					0,
					{},
					curr.search,
					false,
					false,
					null,
					null,
				);

				match = prev[id];
			}

			prev[id] = this.addToGroupedWrit(
				match,
				curr,
				executionStates,
				debts,
				matterFileType,
				matterId,
				requisitions,
			);
			return prev;
		}, grouped);

		// merge executions data so that all executions when bought show in each tab
		Object.values(result).forEach((group) => {
			const mergedExecutions: { [id: string]: NamesViewModel } = {};

			if (!group.writSearches) {
				return;
			}
			// build merged collection
			Object.values(group.writSearches).forEach((writ) => {
				// remove any failed searches to prevent theme from adding to search count
				if (indexedSavedWrits[writ.id].error)
					delete result[group.id].writSearches[writ.id];

				if (!writ.executions) {
					return;
				}

				Object.entries(writ.executions).forEach(([key, value]) => {
					if (!mergedExecutions[key] && value.execution?.certificate) {
						mergedExecutions[key] = value;
					}
				});

				Object.entries(writ.executions).forEach(([key, value]) => {
					if (mergedExecutions[key]) {
						writ.executions[key] = mergedExecutions[key];
					}
				});
			});
		});

		return cloneDeep(result);
	}

	static buildWritSearch = (
		writ: SavedWrit,
		executionStates: PurchaseStates,
		debts: Debt[],
		matterFileType: MatterFileType,
		matterId: string,
		requisitions: MatterRequisitionPrecedent[],
	): WritSearch => {
		return {
			id: writ.id,
			date: new Date(writ.writ?.certificate?.request_date)?.getTime(),
			certificate: writ.writ?.certificate,
			executions: this.getResultsVM(
				executionStates,
				writ,
				debts,
				matterFileType,
				matterId,
				requisitions,
			),
			selected: false,
		};
	};

	static clientName(client: PersonClient | BusinessClient): string {
		if (client.name) {
			return client.name;
		}

		const _client = client as PersonClient;

		return [_client.firstName, _client.middleNames, _client.lastName]
			.filter((x) => !!x)
			.join(' ');
	}

	static getEmptyClientWrit = (client: Clients): GroupedWrit => {
		const name = this.clientName(client);
		let search: WritSearchItem;

		if (client.type === ClientType.PERSON) {
			search = {
				type: WritSearchType.PERSON,
				item: {
					firstName: [
						(client as PersonClient).firstName,
						(client as PersonClient).middleNames,
					]
						?.filter((x) => !!x)
						.join(' '),
					lastName: (client as PersonClient).lastName,
				},
			};
		} else {
			search = {
				type: WritSearchType.COMPANY,
				item: {
					name,
				},
			};
		}

		return this.buildGroupedWrit(
			name.toLowerCase(),
			name,
			0,
			{},
			search,
			false,
			true,
			client.id,
			client.clientTypes,
		);
	};

	static addToGroupedWrit = (
		group: GroupedWrit,
		savedWrit: SavedWrit,
		executionStates: PurchaseStates,
		debts: Debt[],
		matterFileType: MatterFileType,
		matterId: string,
		requisitions: MatterRequisitionPrecedent[],
	): GroupedWrit => {
		if (
			savedWrit.status !== WritSearchStatus.FAILED &&
			savedWrit.status !== WritSearchStatus.SEARCHING
		) {
			group.searchesCount += 1;
			group.dirty = false;
		}
		if (savedWrit.status === WritSearchStatus.SEARCHING) {
			group.searching = true;
		} else {
			group.dirty = false;
		}
		group.writSearches[savedWrit.id] = this.buildWritSearch(
			savedWrit,
			executionStates,
			debts,
			matterFileType,
			matterId,
			requisitions,
		);
		return group;
	};

	static buildGroupedWrit = (
		id: string,
		name: string,
		searchesCount: number,
		writs: { [id: string]: WritSearch },
		search: WritSearchItem,
		searching: boolean,
		dirty: boolean,
		clientId: string,
		clientTypes: MatterClientType[],
	): GroupedWrit => ({
		id,
		name,
		lowercaseName: name.toLowerCase(),
		searchesCount: searchesCount,
		writSearches: writs,
		search: search,
		searching,
		clientId,
		clientTypes,
		dirty,
	});

	static buildExecution = (
		executionId: string,
		executionDetails: WritName,
		executionStates: PurchaseStates,
		matterFileType: MatterFileType,
		matterId: string,
		requisitions: MatterRequisitionPrecedent[],
		debts: Debt[],
	) => {
		const requisition = requisitions?.find(
			(x) => x.fields?.executionsNumber?.value === executionId,
		);
		const debt = debts?.find((x) => x.executionNumber === executionId);
		const executionState = executionStates?.[executionId];
		const judgment = executionDetails?.execution?.judgment_infos?.length > 0;
		const judgmentAmount =
			executionDetails?.execution?.judgment_infos?.reduce((prev, curr) => {
				return prev + dollarsToCents(curr.Judgmentamount);
			}, 0) ?? 0;

		const writName: NamesViewModel = {
			...executionDetails,
			allowPurchase:
				!executionDetails?.execution &&
				executionState?.state !== PurchaseStateType.PURCHASING,
			purchasing:
				executionStates?.[executionId]?.state === PurchaseStateType.PURCHASING,
			purchased:
				executionDetails?.execution?.certificate &&
				executionStates?.[executionId]?.state !== PurchaseStateType.PURCHASING,
			purchaseError:
				executionStates?.[executionId]?.state === PurchaseStateType.ERROR,
			error: executionStates?.[executionId]?.error,
			showAddRequisition:
				!requisition && matterFileType !== MatterFileType.SALE,
			showLinkedRequisition: !!requisition,
			requisitionId: requisition?.id,
			requsitionLinkUrl: requisition
				? UrlService.Matter_Documents_Requisitions(matterId)
				: '',
			showAddBillAndDebt: !debt && judgment,
			showLinkedBillAndDebt: !!debt,
			billAndDebtLinkUrl: debt ? UrlService.Matter_Debts(debt.id) : '',
			debtId: debt?.id,
			judgment,
			judgmentAmount,
		};

		return writName;
	};

	static getResultsVM(
		executionStates: PurchaseStates,
		savedWrit: SavedWrit,
		debts: Debt[],
		matterFileType: MatterFileType,
		matterId: string,
		requisitions: MatterRequisitionPrecedent[],
	): { [id: string]: NamesViewModel } {
		if (!savedWrit || !savedWrit.writ?.names) {
			return {};
		}

		return Object.values(savedWrit.writ?.names)?.reduce(
			(prev, curr) => {
				prev[curr.execution_number] = this.buildExecution(
					curr.execution_number,
					curr,
					executionStates,
					matterFileType,
					matterId,
					requisitions,
					debts,
				);
				return prev;
			},
			{} as { [id: string]: NamesViewModel },
		);
	}
}
